import { Currencies } from '@/entities/curency'
import { Roles } from '@/entities/roles'
import { TopupModel, TopupModelMerchant } from '@/entities/topup'
import { TopupStatus } from '@/entities/topups'
import { BaseButton } from '@/shared/components'
import { formatCurrency } from '@/shared/lib/currency'
import { formatDate } from '@/shared/lib/date'
import { useIsMobile } from '@/shared/utils/useIsMobile/useIsMobile'
import DisputIcon from '@/shared/assets/icons/dashboard/orders.svg?react'
import { DisputModel, DisputStatus } from '@/features/orders'
import { useManualConfirmTimer } from '@/entities/topup/model/hooks/useManualConfirmTimer'

type Props = {
  data: TopupModel | TopupModelMerchant
  onConfirm?: (id: string) => void
  userRole: Roles | null
  openModal: () => void
  buttonIsVisible: boolean
  disputId?: string
  disputData?: DisputModel
}

export const AdaptiveDealBanner = ({
  data,
  onConfirm,
  userRole,
  openModal,
  buttonIsVisible,
  disputId,
  disputData,
}: Props) => {
  const isConfirmed = data.confirmedAt !== '0001-01-01T00:00:00' && data.confirmedAt !== null
  const isMobile = useIsMobile()

  const isMerchant = userRole === Roles.Merchant

  const hasDisput = disputId && disputData
  const isDisputSatisfied = disputData?.status === DisputStatus.Satisfied

  // Хук для таймера ручного подтверждения
  const { timeRemaining, isManualConfirmAvailable } = useManualConfirmTimer({ data, userRole })

  const info = isMerchant
    ? [
        { label: 'Дата создания', data: formatDate(data.createdAt, 'long', {}) },
        {
          label: 'Дата принятия',
          data: isConfirmed ? formatDate(data.confirmedAt, 'long', {}) : 'Не принят',
        },
        {
          label: 'Комиссия',
          data: formatCurrency((data as TopupModelMerchant).commission, Currencies.USDT),
        },
        { label: 'Курс USDT', data: formatCurrency(data.exchangeRate, Currencies.RUB) },
      ]
    : [
        {
          label: 'Дата принятия',
          data: isConfirmed ? formatDate(data.confirmedAt, 'long', {}) : 'Не принят',
        },
        { label: 'Прибыль', data: formatCurrency((data as TopupModel).profit, Currencies.USDT) },
        { label: 'Курс USDT', data: formatCurrency(data.exchangeRate, Currencies.RUB) },
      ]

  return (
    <>      
        {isMobile && isManualConfirmAvailable() && (
          <div className="flex flex-1 mb-6">
            <BaseButton radius="lg" className="w-full" onClick={() => onConfirm?.(data.id ?? '')}>
              <span>Подтвердить</span>
              <span className="text-primary-dark-disabled"> • {timeRemaining} мин</span>
            </BaseButton>
          </div>
        )}
      
      {isMobile ? (

        <div className="flex flex-col gap-6 bg-light-purple dark:bg-dark-purple px-5 py-6 rounded-2xl">
          <div className="flex flex-col gap-2 pb-4 border-b border-b-card-border dark:border-b-card-border-dark">
            <div>
              <span className="mb-1 text-text-secondary text-sm">Сумма сделки:</span>
              <div className="flex justify-between items-center">
                <span className="font-medium">
                  {formatCurrency(data.amountTo, data.currencyTo)}
                </span>
                <span className="font-klein font-medium text-text-secondary text-sm">
                  {formatCurrency(data.amountFrom, data.currencyFrom)}
                </span>
              </div>
            </div>
            {hasDisput && (
              <>
                <div>
                  <span className="mb-1 text-text-secondary text-sm">Сумма спора:</span>
                  <div className="flex justify-between items-center">
                    <span className="font-medium">
                      {formatCurrency(disputData.disputAmountFromMerchantUsdt, Currencies.USDT)}
                    </span>
                    <span className="font-klein font-medium text-text-secondary text-sm">
                      {formatCurrency(disputData.disputAmountFromMerchant, Currencies.RUB)}
                    </span>
                  </div>
                </div>
                {isDisputSatisfied && (
                  <div>
                    <span className="mb-1 text-text-secondary text-sm">Итоговая сумма:</span>
                    <div className="flex justify-between items-center">
                      <span className="font-medium">
                        {formatCurrency(
                          disputData.disputAmountFromTraderUsdt ??
                            disputData.disputAmountFromMerchantUsdt,
                          Currencies.USDT
                        )}
                      </span>
                      <span className="font-klein font-medium text-text-secondary text-sm">
                        {formatCurrency(
                          disputData.disputAmountFromTrader ?? disputData.disputAmountFromMerchant,
                          Currencies.RUB
                        )}
                      </span>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="flex flex-col gap-[10px]">
            {info.map((el, i) => (
              <div
                key={i}
                className="flex flex-col flex-1 pb-4 last:pb-0 last:border-0 border-b border-b-card-border dark:border-b-card-border-dark"
              >
                <span className="text-text-secondary text-sm">{el.label}</span>
                <span className="font-klein font-medium">{el.data}</span>
              </div>
            ))}
          </div>
          {userRole !== Roles.Merchant && data.status === TopupStatus.WaitingPayment && (
            <BaseButton radius="lg" onClick={() => onConfirm?.(data.id ?? '')}>
              Подтвердить
            </BaseButton>
          )}
          {userRole === Roles.Merchant && buttonIsVisible && (
            <BaseButton radius="lg" icon={<DisputIcon />} iconPosition="right" onClick={openModal}>
              Создать спор
            </BaseButton>
          )}
        </div>
      ) : (
        <div className="flex bg-light-purple dark:bg-dark-purple p-6 rounded-2xl">
          {info.map((el, i) => (
            <div
              key={i}
              className="flex flex-col flex-1 pr-6 last:pr-0 pl-6 first:pl-0 last:border-0 border-r border-r-card-border dark:border-r-card-border-dark"
            >
              <span className="text-text-secondary text-sm">{el.label}</span>
              <span className="font-klein font-medium">{el.data}</span>
            </div>
          ))}
        </div>
      )}
    </>
  )
}
